import { CloseOutlined, CloudUploadOutlined, LoadingOutlined } from "@ant-design/icons";
import { Affix, Button, Progress, Tooltip, Typography } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useRestoreInspirationUploadProgress } from "../../modules/inspirationGallery/hooks/use-restore-inspiration-upload-progress";
import { dismissInspirationGalleryProgressFromUi } from "../../modules/inspirationGallery/service/inspiration-gallery-upload-sse";
import "./upload-progress-bar.css";

const UploadProgressBar = () => {
    useRestoreInspirationUploadProgress();
    const dispatch = useDispatch();

    const uploads = useSelector((state) => state.uploadProgressSlice.uploads);
    const bulkJob = useSelector((state) => state.uploadProgressSlice.bulkGalleryJob);

    const bulkOverallPercent =
        bulkJob && bulkJob.total > 0
            ? Math.min(
                  100,
                  Math.round(
                      ((bulkJob.completed + bulkJob.currentPercent / 100) / bulkJob.total) * 100
                  )
              )
            : 0;

    if (bulkJob) {
        return (
            <Affix offsetBottom={0}>
                <div className="upload-progress-bar upload-progress-bar--bulk">
                    <div className="upload-progress-bar__head">
                        <div className="upload-progress-bar__title-block">
                            <span className="upload-progress-bar__title-icon" aria-hidden>
                                {bulkOverallPercent < 100 ? (
                                    <LoadingOutlined spin />
                                ) : (
                                    <CloudUploadOutlined />
                                )}
                            </span>
                            <Typography.Text className="upload-progress-bar__title">
                                Bulk inspiration upload
                            </Typography.Text>
                        </div>
                        <Tooltip title="Close progress panel">
                            <Button
                                type="text"
                                size="small"
                                className="upload-progress-bar__close"
                                aria-label="Dismiss progress"
                                icon={<CloseOutlined />}
                                onClick={() =>
                                    dismissInspirationGalleryProgressFromUi(dispatch, {
                                        type: "bulk",
                                    })
                                }
                            />
                        </Tooltip>
                    </div>
                    <Typography.Text type="secondary" className="upload-progress-bar__meta">
                        <strong>{bulkJob.completed}</strong> of {bulkJob.total} complete
                        {bulkJob.currentIndex > 0 ? (
                            <>
                                {" "}
                                · item <strong>{bulkJob.currentIndex}</strong> of {bulkJob.total}
                            </>
                        ) : null}
                    </Typography.Text>
                    {bulkJob.currentTitle ? (
                        <Typography.Text ellipsis className="upload-progress-bar__current-title">
                            {bulkJob.currentTitle}
                        </Typography.Text>
                    ) : null}
                    {bulkJob.currentFile ? (
                        <Typography.Text
                            type="secondary"
                            ellipsis
                            className="upload-progress-bar__file"
                            title={bulkJob.currentFile}
                        >
                            {bulkJob.currentFile}
                        </Typography.Text>
                    ) : null}
                    <Progress
                        percent={bulkOverallPercent}
                        size="small"
                        status={bulkOverallPercent >= 100 ? "success" : "active"}
                        strokeColor={bulkOverallPercent >= 100 ? undefined : "#1677ff"}
                    />
                </div>
            </Affix>
        );
    }

    if (!uploads.length) return null;

    return (
        <Affix offsetBottom={0}>
            <div className="upload-progress-bar upload-progress-bar--single">
                <div className="upload-progress-bar__head">
                    <div className="upload-progress-bar__title-block">
                        <span className="upload-progress-bar__title-icon" aria-hidden>
                            {uploads.some((u) => u.percent < 100) ? (
                                <LoadingOutlined spin />
                            ) : (
                                <CloudUploadOutlined />
                            )}
                        </span>
                        <Typography.Text className="upload-progress-bar__title">
                            {uploads.length === 1 ? "Publishing gallery" : "Publishing galleries"}
                        </Typography.Text>
                    </div>
                </div>
                {uploads.map((u) => (
                    <div key={u.id} className="upload-progress-bar__row">
                        <div className="upload-progress-bar__row-head">
                            <div className="upload-progress-bar__row-label" title={u.currentFile}>
                                <b>{u.currentFile || "Processing…"}</b>
                            </div>
                            <Tooltip title="Hide this row">
                                <Button
                                    type="text"
                                    size="small"
                                    className="upload-progress-bar__close upload-progress-bar__close--row"
                                    aria-label="Dismiss this upload"
                                    icon={<CloseOutlined />}
                                    onClick={() =>
                                        dismissInspirationGalleryProgressFromUi(dispatch, {
                                            type: "single",
                                            uploadId: u.id,
                                        })
                                    }
                                />
                            </Tooltip>
                        </div>
                        <Progress
                            percent={u.percent}
                            size="small"
                            status={u.percent === 100 ? "success" : "active"}
                            strokeColor={u.percent === 100 ? undefined : "#1677ff"}
                        />
                    </div>
                ))}
            </div>
        </Affix>
    );
};

export default UploadProgressBar;
